import { Field, Form, Formik } from "formik";
import noop from "lodash/noop";
import PropTypes from "prop-types";
import React from "react";
import { Col, Row } from "react-bootstrap";
import * as Application from "redux-store/applications";
import { Header } from "ui/header";
import { getAppParamKey } from "util/application";
import { sortByDisplayNameAsc } from "util/sort-by-prop";

import { OptionalParamsSelector } from "./optional-params-selector";
import { ParamFormField } from "./param-form-field";
import ParamUtils, { PRIORITY } from "./utils";

class AppParameterForm extends React.PureComponent {
  static get propTypes() {
    return {
      app: PropTypes.object.isRequired,
      deviceParams: PropTypes.arrayOf(PropTypes.object),
      formErrors: PropTypes.object.isRequired,
      getFormApi: PropTypes.func.isRequired,
      network: PropTypes.object.isRequired,
      networkParams: PropTypes.arrayOf(PropTypes.object).isRequired,
      priority: PropTypes.oneOf([PRIORITY.DEVICE, PRIORITY.NETWORK]).isRequired,
      readonly: PropTypes.bool,
      schemas: PropTypes.arrayOf(PropTypes.object),
      systemManagedParams: PropTypes.array,
    };
  }

  static get defaultProps() {
    return {
      readonly: false,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      active: this._getActiveFields(props),
    };

    this.form = null;
    this.initialValues = this._getInitialValues(props);
    // Can't use state, because field race conditions.
    this.isOverriding = {};

    this.addOptionalParam = this.addOptionalParam.bind(this);
    this.getFormApi = this.getFormApi.bind(this);
    this.getFormValues = this.getFormValues.bind(this);
    this._isFieldDisabled = this._isFieldDisabled.bind(this);
    this._renderOptionalAppParams = this._renderOptionalAppParams.bind(this);
    this._renderRequiredAppParams = this._renderRequiredAppParams.bind(this);
    this._setRef = this._setRef.bind(this);

    props.getFormApi(this.getFormApi);
  }

  _collectErrors(app, formErrors, name) {
    return Object.entries(formErrors || {})
      .filter(([path, _value]) => path === `${app.id}.${name}`)
      .reduce((acc, [_path, value]) => value, []);
  }

  _getInitialValues(props) {
    const { schemas, deviceParams, networkParams } = props;

    return schemas.reduce((acc, { name, default_value: defaultValue }) => {
      acc[name] = ParamUtils.getPriorityValue(name, {
        deviceParams,
        networkParams,
        defaultValue,
      });

      return acc;
    }, {});
  }

  _isFieldDisabled(readOnly, schema = {}) {
    if (readOnly) {
      return true;
    }

    const { app, systemManagedParams } = this.props;

    if (!systemManagedParams) {
      return false;
    }

    if (!Application.systemManaged(app)) {
      return false;
    }

    return systemManagedParams.includes(getAppParamKey(app.name, schema.name));
  }

  getFormValues() {
    const { active } = this.state;
    const formikBag = this.form.getFormikBag();
    const formValues = formikBag.values;

    const overrideOff = Object.entries(this.isOverriding)
      .filter(([_name, override]) => !override)
      .reduce((acc, [name, _override]) => {
        acc.add(name);
        return acc;
      }, new Set());

    return Object.keys(formValues)
      .filter((name) => {
        if (overrideOff.has(name)) {
          return false;
        }

        return active.includes(name);
      })
      .reduce((acc, name) => {
        acc[name] = formValues[name];
        return acc;
      }, {});
  }

  getFormApi() {
    return {
      form: this.form,
      getFormValues: this.getFormValues,
    };
  }

  _getActiveFields(props) {
    const { deviceParams, networkParams, schemas } = props;

    const requiredFields = schemas.filter((s) => s.required).map((s) => s.name);
    const optionalFields = schemas
      .filter((schema) => {
        const { name, default_value: defaultValue } = schema;

        const deviceParam = ParamUtils.getDeviceLevelParam(name, deviceParams);
        const networkParam = ParamUtils.getNetworkLevelParam(
          name,
          networkParams,
        );
        const priorities = ParamUtils.buildPriorities(
          defaultValue,
          networkParam,
          deviceParam,
        );

        return priorities.length > 0;
      })
      .map((s) => s.name);

    const uniqueFields = new Set([...requiredFields, ...optionalFields]);
    const fields = Array.from(uniqueFields);

    return fields.sort();
  }

  _remove(name) {
    const { schemas } = this.props;
    const { active: current } = this.state;

    const canRemove = schemas.some((s) => s.name === name && !s.required);
    if (!canRemove) {
      return;
    }

    const active = current.filter((a) => a !== name);
    this.setState({ active });

    this.resetFieldValue(name);
  }

  _onOverride(name, isOverriding) {
    this.isOverriding[name] = isOverriding;

    const priority = isOverriding ? PRIORITY.MAX : this.props.priority;

    this.resetFieldValue(name, priority);
  }

  _setRef(el) {
    if (el) {
      this.form = el;
    }
  }

  addOptionalParam(schema) {
    const { active } = this.state;

    this.setState({ active: [...active, schema.name] });
  }

  resetFieldValue(name, priority = PRIORITY.MAX) {
    const { deviceParams, networkParams, schemas } = this.props;
    const schema = schemas.find((s) => s.name === name);
    const { default_value: defaultValue } = schema;

    const initialValue = ParamUtils.getPriorityValue(name, {
      deviceParams,
      networkParams,
      defaultValue,
      priority,
    });

    // Possible the ref is not ready.
    if (this.form) {
      this.form.setFieldValue(name, initialValue);
    }
  }

  _renderRequiredAppParams() {
    const { schemas } = this.props;
    const requiredParamSchemas = schemas
      .filter((s) => s.required)
      .sort(sortByDisplayNameAsc);

    if (requiredParamSchemas.length === 0) {
      return null;
    }

    const rowStyle = {
      display: "flex",
      flexWrap: "wrap",
      gap: "16px",
      position: "relative",
      paddingLeft: "16px",
      paddingRight: "16px",
    };

    return (
      <div style={rowStyle}>
        <div style={{ flex: "0 0 100%", maxWidth: "100%" }}>
          <Header title="Required parameters" />
        </div>
        {requiredParamSchemas.map(this._renderAppParam, this)}
      </div>
    );
  }

  _renderOptionalAppParams() {
    const { schemas } = this.props;
    const { active } = this.state;
    const optionalParamSchemas = active
      .map((a) => schemas.find((s) => s.name === a && !s.required))
      .filter((s) => s)
      .sort(sortByDisplayNameAsc);

    if (optionalParamSchemas.length === 0) {
      return null;
    }

    const rowStyle = {
      display: "flex",
      flexWrap: "wrap",
      gap: "16px",
      position: "relative",
      paddingLeft: "16px",
      paddingRight: "16px",
    };

    return (
      <div style={rowStyle}>
        <div style={{ flex: "0 0 100%", maxWidth: "100%" }}>
          <Header title="Optional parameters" />
        </div>
        <div style={{ flex: "0 0 100%", maxWidth: "100%" }}>
          <OptionalParamsSelector
            active={active}
            schemas={schemas}
            onSelect={this.addOptionalParam}
          />
        </div>
        {optionalParamSchemas.map(this._renderAppParam, this)}
      </div>
    );
  }

  _renderAppParam(schema) {
    const {
      app,
      deviceParams,
      formErrors,
      network,
      networkParams,
      priority,
      readonly,
    } = this.props;
    const { name } = schema;
    const deviceParam = deviceParams.find((p) => p.name === name);
    const errors = this._collectErrors(app, formErrors, name);
    const isDisabled = this._isFieldDisabled(readonly, schema);
    const columnStyle = {
      flex: "1 1 calc(50% - 16px)",
      maxWidth: "calc(50% - 16px)",
      display: "flex",
      flexDirection: "column",
    };

    return (
      <div key={`param-${name}`} style={columnStyle}>
        <Field
          component={ParamFormField}
          deviceParam={deviceParam}
          errors={errors}
          name={name}
          network={network}
          networkParams={networkParams}
          onOverride={this._onOverride.bind(this, name)}
          onRemove={this._remove.bind(this, name)}
          priority={priority}
          readOnly={isDisabled}
          schema={schema}
        />
      </div>
    );
  }

  render() {
    return (
      <Row>
        <Formik
          initialValues={this.initialValues}
          onSubmit={noop}
          ref={this._setRef}
        >
          <Form>
            {this._renderRequiredAppParams()}
            {this._renderOptionalAppParams()}
          </Form>
        </Formik>
      </Row>
    );
  }
}

export { AppParameterForm };
